import { useState } from "react";
import * as d3 from "d3";

export type ChartPoint = {
  bucket: Date;
  value: number;
  n: number;
  lo?: number; // optional spread band (e.g. p25)
  hi?: number; // optional spread band (e.g. p75)
  provisional?: boolean; // immature / under-complete bucket — drawn faded + dashed
};

const MARGIN = { top: 18, right: 16, bottom: 30, left: 44 };

/**
 * A metric-over-time line chart. Draws a value line with an optional lo–hi band,
 * encodes cohort size as point opacity, dashes provisional (not-yet-mature)
 * buckets, and shows an instant tooltip on hover (generous hit targets).
 */
export function TrendChart({
  points,
  width,
  height,
  label,
  unit = "d",
}: {
  points: ChartPoint[];
  width: number;
  height: number;
  label?: string;
  unit?: string;
}) {
  const [hover, setHover] = useState<{
    left: number;
    top: number;
    p: ChartPoint;
  } | null>(null);

  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;

  if (points.length === 0) {
    return (
      <svg width={width} height={height}>
        <text
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          fontSize={12}
          fill="#999"
        >
          No data in scope
        </text>
      </svg>
    );
  }

  const x = d3
    .scaleTime()
    .domain(d3.extent(points, (p) => p.bucket) as [Date, Date])
    .range([0, innerW]);
  const hasBand = points.some((p) => p.lo != null && p.hi != null);
  const yMax = d3.max(points, (p) => Math.max(p.value, p.hi ?? p.value)) ?? 0;
  const y = d3.scaleLinear().domain([0, yMax]).range([innerH, 0]).nice();
  // Point colour encodes cohort size (darker = more episodes); exact n on hover.
  // Domained on the actual n-range so contrast shows even when cohorts are similar.
  const nMin = d3.min(points, (p) => p.n) ?? 0;
  const nMax = d3.max(points, (p) => p.n) ?? 1;
  const color = (n: number) => {
    const t = nMax === nMin ? 1 : (n - nMin) / (nMax - nMin);
    return d3.interpolateBlues(0.4 + 0.6 * t);
  };

  const line = d3
    .line<ChartPoint>()
    .x((p) => x(p.bucket))
    .y((p) => y(p.value));
  const band = d3
    .area<ChartPoint>()
    .x((p) => x(p.bucket))
    .y0((p) => y(p.lo ?? p.value))
    .y1((p) => y(p.hi ?? p.value));

  // Provisional buckets are the recent tail — draw them as a dashed continuation.
  const firstProv = points.findIndex((p) => p.provisional);
  const solid = firstProv === -1 ? points : points.slice(0, firstProv);
  const dashed =
    firstProv === -1 ? [] : points.slice(Math.max(0, firstProv - 1));

  const xTicks = x.ticks(Math.min(points.length, 6));
  const yTicks = y.ticks(4);
  const fmtMonth = d3.timeFormat("%b %y");
  const fmtFull = d3.timeFormat("%b %Y");

  return (
    <div style={{ position: "relative", width, height }}>
      <svg width={width} height={height}>
        <g
          transform={`translate(${String(MARGIN.left)},${String(MARGIN.top)})`}
        >
          {yTicks.map((t) => (
            <g key={t} transform={`translate(0,${String(y(t))})`}>
              <line
                x1={0}
                x2={innerW}
                stroke="#eee"
                shapeRendering="crispEdges"
              />
              <text
                x={-8}
                dy="0.32em"
                textAnchor="end"
                fontSize={10}
                fill="#666"
              >
                {t}
                {unit === "%" ? "%" : ""}
              </text>
            </g>
          ))}

          {xTicks.map((t, i) => (
            <text
              key={i}
              x={x(t)}
              y={innerH + 16}
              textAnchor="middle"
              fontSize={10}
              fill="#666"
            >
              {fmtMonth(t)}
            </text>
          ))}

          {hasBand && (
            <path d={band(points) ?? undefined} fill="#e2e8f0" opacity={0.7} />
          )}

          {solid.length > 1 && (
            <path
              d={line(solid) ?? undefined}
              fill="none"
              stroke="#94a3b8"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          )}
          {dashed.length > 1 && (
            <path
              d={line(dashed) ?? undefined}
              fill="none"
              stroke="#94a3b8"
              strokeWidth={2}
              strokeDasharray="4,3"
              opacity={0.7}
              strokeLinejoin="round"
            />
          )}

          {points.map((p, i) => (
            <circle
              key={i}
              cx={x(p.bucket)}
              cy={y(p.value)}
              r={3}
              fill={color(p.n)}
            />
          ))}

          {/* Hover highlight ring */}
          {hover && (
            <circle
              cx={x(hover.p.bucket)}
              cy={y(hover.p.value)}
              r={7}
              fill="none"
              stroke="#1f2937"
              strokeWidth={2}
            />
          )}

          {/* Generous transparent hit targets for an easy, instant hover */}
          {points.map((p, i) => (
            <circle
              key={`hit-${String(i)}`}
              cx={x(p.bucket)}
              cy={y(p.value)}
              r={12}
              fill="transparent"
              style={{ cursor: "pointer" }}
              onMouseEnter={() => {
                setHover({
                  left: MARGIN.left + x(p.bucket),
                  top: MARGIN.top + y(p.value),
                  p,
                });
              }}
              onMouseLeave={() => {
                setHover(null);
              }}
            />
          ))}

          {label && (
            <text x={0} y={-6} fontSize={11} fontWeight={600} fill="#333">
              {label}
            </text>
          )}
        </g>
      </svg>

      {hover && (
        <div
          style={{
            position: "absolute",
            left: hover.left,
            top: hover.top - 12,
            transform: "translate(-50%, -100%)",
            background: "#1f2937",
            color: "white",
            fontSize: 11,
            lineHeight: 1.4,
            padding: "5px 8px",
            borderRadius: 6,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            zIndex: 10,
          }}
        >
          <div style={{ fontWeight: 600 }}>{fmtFull(hover.p.bucket)}</div>
          <div>
            {hover.p.value}
            {unit} · n={hover.p.n}
            {hover.p.provisional ? " · provisional" : ""}
          </div>
        </div>
      )}
    </div>
  );
}
